import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Pencil, Plus, RefreshCw, Trash2, TrendingUp } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const TYPES = [
  { v: "bank", label: "Bank" },
  { v: "wallet", label: "Wallet" },
  { v: "credit_card", label: "Credit Card" },
  { v: "cash", label: "Cash" },
  { v: "mutual_fund", label: "Mutual Fund" },
  { v: "demat", label: "Demat" },
];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function HoldingsTable({ account, onRemoveHolding }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b border-[#D8D4C7] font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">
          <th className="py-2">Symbol</th><th>Qty</th><th>Avg</th><th>LTP</th><th className="text-right">Value</th><th>P/L</th><th></th>
        </tr>
      </thead>
      <tbody>
        {account.holdings.map((h) => {
          const value = h.quantity * h.current_price;
          const pl = value - h.quantity * h.avg_price;
          return (
            <tr key={h.id} className="border-b border-[#EAE7DD]">
              <td className="py-2 font-mono">{h.symbol}</td>
              <td className="num">{h.quantity}</td>
              <td className="num">{formatINR(h.avg_price, { decimals: 2 })}</td>
              <td className="num">{formatINR(h.current_price, { decimals: 2 })}</td>
              <td className="num text-right">{formatINR(value, { decimals: 2 })}</td>
              <td className={`num ${pl >= 0 ? "text-[#2E6F40]" : "text-[#D94F3D]"}`}>{pl >= 0 ? "+" : ""}{formatINR(pl, { decimals: 2 })}</td>
              <td><button data-testid={`delete-holding-${h.id}`} onClick={() => onRemoveHolding(account.id, h.id)} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-3.5 h-3.5" /></button></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SchemeInfo({ account: a }) {
  if (a.type !== "mutual_fund" || !a.scheme_code) return null;
  return (
    <div className="text-xs text-[#6C7565] mt-2 max-w-xs" data-testid={`scheme-info-${a.id}`}>
      <span className="block">{a.scheme_name}</span>
      <span className="font-mono text-[10px] uppercase tracking-widest block mt-1">
        {a.units} units{a.nav ? ` · NAV ${formatINR(a.nav, { decimals: 2 })}` : ""}{a.nav_date ? ` (${a.nav_date})` : ""}
      </span>
    </div>
  );
}

function OpeningInfo({ account: a }) {
  const navLinked = a.type === "mutual_fund" && a.scheme_code;
  if (a.type === "demat" || navLinked || a.opening_balance === undefined) return null;
  return (
    <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mt-1" data-testid={`opening-balance-${a.id}`}>
      Opening {formatINR(a.opening_balance)}{a.opening_date ? ` as on ${fmtDate(a.opening_date)}` : ""}
    </div>
  );
}

function DematSection({ account: a, onAddHolding, onRemoveHolding }) {
  return (
    <div className="mt-6 border-t border-[#EAE7DD] pt-4">
      <div className="flex justify-between items-center mb-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] flex items-center gap-2">
          <TrendingUp className="w-3 h-3" /> Holdings
          {a.prices_updated_at && (
            <span className="normal-case tracking-normal" data-testid={`prices-updated-${a.id}`}>
              · LTP as of {new Date(a.prices_updated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <button data-testid={`add-holding-${a.id}`} onClick={() => onAddHolding(a.id)} className="text-xs uppercase tracking-widest border border-[#1A1F16] px-3 py-1 hover:bg-[#1A1F16] hover:text-[#F7F5F0]">+ Add holding</button>
      </div>
      {(a.holdings || []).length === 0 ? (
        <div className="text-sm text-[#6C7565] italic">No holdings yet.</div>
      ) : (
        <HoldingsTable account={a} onRemoveHolding={onRemoveHolding} />
      )}
    </div>
  );
}

function AccountCard({ account: a, onRemove, onEdit, onAddHolding, onRemoveHolding }) {
  const isDemat = a.type === "demat";
  const dematValue = isDemat ? (a.holdings || []).reduce((s, h) => s + h.quantity * h.current_price, 0) : 0;
  return (
    <div className="border border-[#D8D4C7] bg-white p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{TYPES.find((t) => t.v === a.type)?.label}</div>
          <h3 className="font-serif text-3xl mt-1">{a.name}</h3>
          {a.institution && <div className="text-sm text-[#6C7565] mt-1">{a.institution}</div>}
          <SchemeInfo account={a} />
        </div>
        <div className="text-right">
          <div className="num font-serif text-3xl">{formatINR(isDemat ? dematValue : a.balance)}</div>
          <OpeningInfo account={a} />
          <div className="flex items-center justify-end gap-4 mt-2">
            <button data-testid={`edit-account-${a.id}`} onClick={() => onEdit(a)} className="text-[#6C7565] hover:text-[#1A1F16] inline-flex items-center gap-1 text-xs uppercase tracking-widest"><Pencil className="w-3 h-3" /> Edit</button>
            <button data-testid={`delete-account-${a.id}`} onClick={() => onRemove(a.id)} className="text-[#6C7565] hover:text-[#D94F3D] inline-flex items-center gap-1 text-xs uppercase tracking-widest"><Trash2 className="w-3 h-3" /> Remove</button>
          </div>
        </div>
      </div>
      {isDemat && <DematSection account={a} onAddHolding={onAddHolding} onRemoveHolding={onRemoveHolding} />}
    </div>
  );
}

function AccountFormModal({ form, setForm, onSubmit, onClose, editing }) {
  const [mfQuery, setMfQuery] = useState("");
  const [mfResults, setMfResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (mfQuery.trim().length < 3) { setMfResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/mf/search?q=${encodeURIComponent(mfQuery.trim())}`);
        setMfResults(data.results || []);
      } catch { setMfResults([]); }
      finally { setSearching(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [mfQuery]);

  const pickScheme = (s) => {
    setForm({ ...form, scheme_code: s.scheme_code, scheme_name: s.scheme_name });
    setMfQuery("");
    setMfResults([]);
  };

  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md" data-testid="account-form">
        <h3 className="font-serif text-3xl mb-6">{editing ? "Edit account" : "New account"}</h3>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Type</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="account-type" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </label>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Name</span>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" placeholder="e.g. HDFC Savings" />
        </label>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Institution</span>
          <input value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} data-testid="account-institution" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
        </label>
        {form.type === "mutual_fund" && (
          <div className="mb-4 border border-[#D8D4C7] bg-white/60 p-4">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">AMFI scheme (optional)</span>
            {form.scheme_code ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm" data-testid="linked-scheme-name">{form.scheme_name}</p>
                  <button type="button" data-testid="unlink-scheme-btn" onClick={() => setForm({ ...form, scheme_code: null, scheme_name: "", units: "" })} className="text-[10px] uppercase tracking-widest text-[#D94F3D] shrink-0">Unlink</button>
                </div>
                <label className="block mt-3">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Units held</span>
                  <input required type="number" step="0.001" min="0" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} data-testid="account-units" className="w-full bg-transparent border-b border-[#1A1F16] py-2 num focus:outline-none" placeholder="e.g. 152.204" />
                </label>
                <span className="text-[10px] text-[#6C7565] block mt-2">Value auto-updates daily: units × latest NAV.</span>
              </>
            ) : (
              <>
                <input value={mfQuery} onChange={(e) => setMfQuery(e.target.value)} data-testid="mf-search-input" className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-sm focus:outline-none" placeholder="Search fund name, e.g. UTI Bluechip" />
                {searching && <span className="text-[10px] text-[#6C7565] block mt-1">Searching…</span>}
                {mfResults.length > 0 && (
                  <ul className="mt-2 max-h-36 overflow-y-auto divide-y divide-[#EAE7DD]" data-testid="mf-search-results">
                    {mfResults.map((s) => (
                      <li key={s.scheme_code}>
                        <button type="button" onClick={() => pickScheme(s)} className="w-full text-left text-xs py-1.5 hover:text-[#A67C00]">{s.scheme_name}</button>
                      </li>
                    ))}
                  </ul>
                )}
                <span className="text-[10px] text-[#6C7565] block mt-2">Link a scheme to track live NAV, or skip and manage the balance yourself.</span>
              </>
            )}
          </div>
        )}
        {form.type !== "demat" && !(form.type === "mutual_fund" && form.scheme_code) && (
          <>
            <label className="block mb-4">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Opening balance (₹)</span>
              <input type="number" step="0.01" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="account-balance" className="w-full bg-transparent border-b border-[#1A1F16] py-2 font-serif text-2xl num focus:outline-none" />
            </label>
            <label className="block mb-6">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">As on date</span>
              <input type="date" value={form.opening_date} onChange={(e) => setForm({ ...form, opening_date: e.target.value })} data-testid="account-opening-date" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
              <span className="text-[10px] text-[#6C7565] block mt-1">Transactions dated before this won't change the balance.</span>
            </label>
          </>
        )}
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" data-testid="account-submit" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628]">Save</button>
        </div>
      </form>
    </div>
  );
}

function HoldingFormModal({ hForm, setHForm, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md" data-testid="holding-form">
        <h3 className="font-serif text-3xl mb-6">Add holding</h3>
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Symbol</span>
            <input required value={hForm.symbol} onChange={(e) => setHForm({ ...hForm, symbol: e.target.value })} data-testid="holding-symbol" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none font-mono uppercase" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Quantity</span>
            <input type="number" step="0.0001" required value={hForm.quantity} onChange={(e) => setHForm({ ...hForm, quantity: e.target.value })} data-testid="holding-qty" className="w-full bg-transparent border-b border-[#1A1F16] py-2 num focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Avg buy ₹</span>
            <input type="number" step="0.01" required value={hForm.avg_price} onChange={(e) => setHForm({ ...hForm, avg_price: e.target.value })} data-testid="holding-avg" className="w-full bg-transparent border-b border-[#1A1F16] py-2 num focus:outline-none" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Current ₹</span>
            <input type="number" step="0.01" required value={hForm.current_price} onChange={(e) => setHForm({ ...hForm, current_price: e.target.value })} data-testid="holding-ltp" className="w-full bg-transparent border-b border-[#1A1F16] py-2 num focus:outline-none" />
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" data-testid="holding-submit" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628]">Add</button>
        </div>
      </form>
    </div>
  );
}

/** Account list state + CRUD actions shared by the page. */
function useAccounts() {
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const load = async () => { const { data } = await api.get("/accounts"); setItems(data); };
  useEffect(() => { load(); }, []);

  const remove = (id) => {
    const a = items.find((x) => x.id === id);
    setConfirm({
      title: "Remove account?",
      detail: `"${a?.name || "This account"}" will be permanently removed${a?.type === "demat" ? " along with its holdings" : ""}.`,
      run: async () => { await api.delete(`/accounts/${id}`); toast.success("Account removed"); load(); },
    });
  };
  const removeHolding = (aid, hid) => {
    setConfirm({
      title: "Remove holding?",
      detail: "This holding will be permanently removed from the demat account.",
      run: async () => { await api.delete(`/accounts/${aid}/holdings/${hid}`); toast.success("Holding removed"); load(); },
    });
  };

  const totalCash = items.filter((a) => a.type !== "demat" && a.type !== "mutual_fund").reduce((s, a) => s + (a.balance || 0), 0);
  const totalMF = items.filter((a) => a.type === "mutual_fund").reduce((s, a) => s + (a.balance || 0), 0);
  const totalDemat = items.filter((a) => a.type === "demat")
    .reduce((s, a) => s + (a.holdings || []).reduce((x, h) => x + h.quantity * h.current_price, 0), 0);

  return { items, load, confirm, setConfirm, remove, removeHolding, totalCash, totalMF, totalDemat };
}

function AccountTotals({ totalCash, totalMF, totalDemat }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D8D4C7] border border-[#D8D4C7]">
      <div className="bg-white p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">Cash & wallets</div>
        <div className="font-serif text-4xl num mt-3" data-testid="total-cash">{formatINR(totalCash)}</div>
      </div>
      <div className="bg-white p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">Mutual funds</div>
        <div className="font-serif text-4xl num mt-3" data-testid="total-mutual-funds">{formatINR(totalMF)}</div>
      </div>
      <div className="bg-white p-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">Demat holdings</div>
        <div className="font-serif text-4xl num mt-3" data-testid="total-demat">{formatINR(totalDemat)}</div>
      </div>
    </div>
  );
}

export default function Accounts() {
  const { items, load, confirm, setConfirm, remove, removeHolding, totalCash, totalMF, totalDemat } = useAccounts();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // account being edited
  const [holdOpen, setHoldOpen] = useState(null); // account id
  const emptyForm = () => ({ name: "", type: "bank", balance: "", institution: "", notes: "", opening_date: new Date().toISOString().slice(0, 10), scheme_code: null, scheme_name: "", units: "" });
  const [form, setForm] = useState(emptyForm());
  const [hForm, setHForm] = useState({ symbol: "", quantity: "", avg_price: "", current_price: "" });

  const closeForm = () => { setOpen(false); setEditing(null); setForm(emptyForm()); };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, type: a.type, institution: a.institution || "", notes: a.notes || "",
      balance: String(a.opening_balance ?? a.balance ?? ""), opening_date: a.opening_date || "",
      scheme_code: a.scheme_code || null, scheme_name: a.scheme_name || "", units: a.units != null ? String(a.units) : "",
    });
    setOpen(true);
  };

  const [refreshing, setRefreshing] = useState(false);
  const hasDemat = items.some((a) => (a.type === "demat" && (a.holdings || []).length > 0) || (a.type === "mutual_fund" && a.scheme_code));

  const refreshPrices = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/prices/refresh");
      if (data.failed?.length) {
        toast.warning(`Updated ${data.updated} holding${data.updated === 1 ? "" : "s"} — no quote for ${data.failed.join(", ")}`);
      } else {
        toast.success(`Live prices updated for ${data.updated} holding${data.updated === 1 ? "" : "s"}`);
      }
      load();
    } catch {
      toast.error("Could not fetch prices — try again in a minute");
    } finally {
      setRefreshing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const opening = parseFloat(form.balance || 0);
    const mfLinked = form.type === "mutual_fund" && form.scheme_code;
    const payload = {
      name: form.name, type: form.type, institution: form.institution, notes: form.notes,
      opening_balance: opening, balance: opening, opening_date: form.opening_date || null,
      scheme_code: mfLinked ? form.scheme_code : null,
      scheme_name: mfLinked ? form.scheme_name : null,
      units: mfLinked ? parseFloat(form.units || 0) : null,
    };
    try {
      if (editing) {
        await api.put(`/accounts/${editing.id}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/accounts", payload);
        toast.success("Account added");
      }
      if (mfLinked) {
        // pull the latest NAV right away so the value shows without waiting for the daily refresh
        try { await api.post("/prices/refresh"); } catch { /* daily auto-refresh will catch up */ }
      }
      closeForm();
      load();
    } catch { toast.error("Failed"); }
  };

  const submitHolding = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounts/${holdOpen}/holdings`, {
        symbol: hForm.symbol.toUpperCase(),
        quantity: parseFloat(hForm.quantity),
        avg_price: parseFloat(hForm.avg_price),
        current_price: parseFloat(hForm.current_price),
      });
      toast.success("Holding added");
      setHoldOpen(null);
      setHForm({ symbol: "", quantity: "", avg_price: "", current_price: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section IV</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Accounts.</h2>
        </div>
        <div className="flex gap-3 flex-wrap">
          {hasDemat && (
            <button data-testid="refresh-prices-btn" onClick={refreshPrices} disabled={refreshing} className="border border-[#1A1F16] px-4 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] disabled:opacity-50 flex items-center gap-2">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Fetching…" : "Refresh prices"}
            </button>
          )}
          <button data-testid="add-account-btn" onClick={() => setOpen(true)} className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> Add account
          </button>
        </div>
      </div>

      <AccountTotals totalCash={totalCash} totalMF={totalMF} totalDemat={totalDemat} />

      {items.length === 0 ? (
        <div className="border border-dashed border-[#D8D4C7] bg-white p-12 text-center text-[#6C7565] font-serif text-2xl" data-testid="accounts-empty">No accounts on the books yet.</div>
      ) : (
        <div className="space-y-4" data-testid="accounts-list">
          {items.map((a) => (
            <AccountCard key={a.id} account={a} onRemove={remove} onEdit={openEdit} onAddHolding={setHoldOpen} onRemoveHolding={removeHolding} />
          ))}
        </div>
      )}

      {open && <AccountFormModal form={form} setForm={setForm} onSubmit={submit} onClose={closeForm} editing={!!editing} />}
      {holdOpen && <HoldingFormModal hForm={hForm} setHForm={setHForm} onSubmit={submitHolding} onClose={() => setHoldOpen(null)} />}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
